import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
  Drawer,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Select,
  MenuItem,
  Badge,
  Avatar,
  Chip,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import MicIcon from '@mui/icons-material/Mic';
import { fuzzyFilter, useVoiceSearch } from './components/smartSearch';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import CloseIcon from '@mui/icons-material/Close';

import config from '../config';

// Brand color for the customer-facing menu (unchanged theme).
const BRAND = '#16243f';

// Order + labels for the QR menu food-type filter dropdown (admin-controlled).
 const FOOD_TYPE_ORDER = [
  ['Veg', 'Veg only'],
  ['NonVeg', 'Non-Veg only'],
  ['Egg', 'Egg only'],
  ['Vegan', 'Vegan only'],
  ['Jain', 'Jain only'],
  ['Liquor', 'Liquor only'],
];

const MEDIA_BASE = (config.API_BASE_URL || '').replace(/\/api$/, '');
const resolveImg = (p) => {
  if (!p) return '';
  if (p.startsWith('data:') || p.startsWith('http')) return p;
  return `${MEDIA_BASE}${p.startsWith('/') ? '' : '/'}${p}`;
};

// Veg / Non-Veg / Egg marker.
function FoodMark({ type }) {
  const color =
    type === 'NonVeg'
      ? '#c0392b'
      : type === 'Egg'
        ? '#e67e22'
        : type === 'Jain'
          ? '#ca8a04'
          : type === 'Vegan'
            ? '#0e9488'
            : // Liquor gets its own mark: it is not a diet, and a bar list
              // shown with a green dot reads as vegetarian food.
              type === 'Liquor'
              ? '#7c3aed'
              : '#0a8a3c';
  return (
    <Box
      sx={{
        width: 16,
        height: 16,
        border: `1.5px solid ${color}`,
        borderRadius: '3px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          bgcolor: color,
          borderRadius: type === 'NonVeg' ? 0 : '50%',
          clipPath:
            type === 'NonVeg' ? 'polygon(50% 0, 100% 100%, 0 100%)' : 'none',
        }}
      />
    </Box>
  );
}

// QR params are packed into an opaque ?d=<base64> token so guests cannot read
// the client id / table name. Legacy plain ?client=&tablename= still works.
const encodeQrParams = (obj) =>
  btoa(encodeURIComponent(new URLSearchParams(obj).toString()));
const decodeQrParams = (raw) => {
  const d = raw.get('d');
  if (!d) return raw;
  try {
    const inner = new URLSearchParams(decodeURIComponent(atob(d)));
    raw.forEach((v, k) => {
      if (k !== 'd') inner.set(k, v);
    });
    return inner;
  } catch {
    return raw;
  }
};

export default function RoomServiceOrder() {
  const [rawParams] = useSearchParams();
  const searchParams = useMemo(() => decodeQrParams(rawParams), [rawParams]);

  const clientFromUrl = searchParams.get('client');
  const tableNameFromUrl = searchParams.get('tablename');

  const [company, setCompany] = useState(null);
  const [companyReady, setCompanyReady] = useState(false);
  // The QR does not belong to any restaurant. Kept apart from a network
  // failure: one is a card to reprint, the other is a phone to move.
  const [unknownQr, setUnknownQr] = useState(false);
  const [selectedTableName, setSelectedTableName] = useState(
    tableNameFromUrl || '',
  );

  const [view, setView] = useState('landing'); // 'landing' | 'menu'

  const [accessMode, setAccessMode] = useState('ScanOrder');
  const [qrEnabled, setQrEnabled] = useState(true);
  // False until table-info has answered for the CURRENT table/room. Ordering
  // stays off in the meantime, so a room QR is never orderable for the
  // moment before the server has said whether the room is checked in.
  const [accessChecked, setAccessChecked] = useState(false);
  // Why the server refused this QR, in words meant for the guest holding the
  // phone. Set only for a ROOM whose stay is not checked in.
  const [blockedReason, setBlockedReason] = useState('');
  const [isRoom, setIsRoom] = useState(false);
  // Area-level online payment (CC Avenue / AllUPI). When true the checkout
  // routes through the gateway instead of a straight pay-at-counter order.
  const [paymentEnabled, setPaymentEnabled] = useState(false);
  // Category ids hidden for THIS table (admin-controlled per QR). Empty = all.
  const [hiddenCatIds, setHiddenCatIds] = useState(() => new Set());
  const canOrder = accessChecked && accessMode === 'ScanOrder' && qrEnabled;

  const [categories, setCategories] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [variantMap, setVariantMap] = useState({});
  const [addonMap, setAddonMap] = useState({}); // #16: itemId -> [{id,name,price}]
  const [itemSearch, setItemSearch] = useState('');
  // The guest can SAY the dish. Same hook, same phonetic matching as the POS.
  const {
    supported: voiceSupported,
    listening,
    toggle: voiceSearch,
  } = useVoiceSearch((text) => setItemSearch(text));
  const [foodFilter, setFoodFilter] = useState('all'); // 'all' | a food-type string
  const [qrFoodTypes, setQrFoodTypes] = useState({ Veg: true, NonVeg: true, Egg: true, Vegan: true, Jain: true, Liquor: true });
  // #2: show item descriptions on the QR menu? (admin display setting; default on)
  const [descOnQr, setDescOnQr] = useState(true);

  const [cart, setCart] = useState({});
  const [cartOpen, setCartOpen] = useState(false);
  const [customize, setCustomize] = useState(null); // #16: { item, variant, addons }
  const [switchOpen, setSwitchOpen] = useState(false);
  const [switchValue, setSwitchValue] = useState('');
  const [notes, setNotes] = useState('');
  const [payOpen, setPayOpen] = useState(false); // gateway-choice dialog

  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // ── Category scroll-spy (left rail) ──
  const scrollRef = useRef(null);
  const sectionRefs = useRef({});
  const [activeCat, setActiveCat] = useState(null);

  // How many dishes of a category are actually DRAWN.
  //
  // The nearest thing React has to yielding from a generator: the first
  // screenful is mounted at once and the rest follow a batch at a time as the
  // guest scrolls. A category of three hundred dishes used to mount three
  // hundred cards in one go - the phone locked up for a second or two, and
  // the dishes seemed to trickle in afterwards.
  //
  // The list is never cut short: whatever is not drawn yet is one scroll (or
  // one idle moment) away, and the count resets whenever the list changes
  // underneath it.
  const DRAW_BATCH = 12;
  const [drawn, setDrawn] = useState(DRAW_BATCH);

  // Reset only when the LIST ITSELF changes - a new search, a different food
  // filter. Every category lives in the one scrolling column, so `activeCat`
  // is not "the open category": it is only which name the left rail lights up,
  // and it changes with every few pixels of scroll.
  //
  // Resetting the draw count on it put the page in a loop. Tapping Whisky set
  // activeCat, which reset drawn to 12, which shrank every category at once,
  // which moved the scroll position onto a different section, which made the
  // scroll-spy set activeCat to THAT one, which reset drawn again - the rail
  // flicking between Whisky and Beer for as long as you watched it.
  useEffect(() => {
    setDrawn(DRAW_BATCH);
  }, [itemSearch, foodFilter]);

  // (the idle top-up lives below groupedItems, which it needs)

  const Companycurrency = company?.currency ?? '₹';

  // ==================== LOAD COMPANY ====================
  useEffect(() => {
    const loadCompany = async () => {
      const client = clientFromUrl || localStorage.getItem('client') || 'Demo';
      try {
        setLoading(true);
        const res = await fetch(
          `${config.API_BASE_URL}/account/initQrCode?client=${encodeURIComponent(client)}`,
        );

        // 404 (and the old empty 200) mean the code in this QR matches no
        // restaurant. A guest cannot do anything about that, but staring at a
        // spinner tells them nothing - so say it, and stop.
        if (res.status === 404 || res.status === 204) {
          setUnknownQr(true);
          return;
        }

        const companyData = await res.json();
        if (!companyData?.id) {
          setUnknownQr(true);
          return;
        }

        sessionStorage.setItem('companyDetails', JSON.stringify(companyData));
        sessionStorage.setItem('companyId', companyData.id);
        setCompany(companyData);
        setCompanyReady(true);
        setSelectedTableName(tableNameFromUrl || '');
      } catch (err) {
        console.error(err);
        setSnackbar({
          open: true,
          message: 'Failed to load company data',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };
    loadCompany();
  }, [clientFromUrl, tableNameFromUrl]);

  // Returning from the payment gateway (?paystatus=success|failed). Show the
  // result, drop the guest straight onto the menu, and clean the URL.
  useEffect(() => {
    const ps = searchParams.get('paystatus');
    if (!ps) return;
    setView('menu');
    setCart({});
    setSnackbar({
      open: true,
      message:
        ps === 'success'
          ? 'Payment successful — your order is confirmed!'
          : 'Payment failed or cancelled. Please try again.',
      severity: ps === 'success' ? 'success' : 'error',
    });
    const c = searchParams.get('client') || '';
    const t = searchParams.get('tablename') || '';
    window.history.replaceState(
      {},
      '',
        `${window.location.pathname}?d=${encodeURIComponent(
          encodeQrParams({ client: c, tablename: t }),
        )}`,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==================== LOAD TABLE ACCESS MODE + HIDDEN CATEGORIES ========
  const loadAccessMode = useCallback(async (companyId, tableName) => {
    // Off while the answer is in flight - a SWITCH to another room must not
    // keep the previous room's permission for even a moment.
    setAccessChecked(false);
    if (!companyId || !tableName) {
      setAccessMode('ScanOrder');
      setQrEnabled(true);
      setPaymentEnabled(false);
      setHiddenCatIds(new Set());
      setBlockedReason('');
      setIsRoom(false);
      setAccessChecked(true);
      return;
    }
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/qrmenu/table-info/${companyId}/${encodeURIComponent(
          tableName,
        )}`,
      );
      const info = await res.json();
      setAccessMode(info?.accessMode || 'ScanOrder');
      setQrEnabled(info?.isQrEnabled !== false);
      setPaymentEnabled(info?.paymentEnabled === true);
      const hidden = info?.hiddenCategoryIds || info?.HiddenCategoryIds || [];
      setHiddenCatIds(new Set(hidden));
      // A room QR with no live check-in comes back blocked, with the sentence
      // to show. Re-asked on every switch, so typing another room number in the
      // Switch dialog is checked too.
      setBlockedReason(info?.blockedReason || '');
      setIsRoom(info?.isRoom === true);
    } catch {
      setAccessMode('ScanOrder');
      setQrEnabled(true);
      setPaymentEnabled(false);
      setHiddenCatIds(new Set());
      setBlockedReason('');
      setIsRoom(false);
    } finally {
      setAccessChecked(true);
    }
  }, []);

  useEffect(() => {
    if (company?.id) loadAccessMode(company.id, selectedTableName);
  }, [company, selectedTableName, loadAccessMode]);

  // ==================== FETCH CATEGORIES + ITEMS + VARIANTS ====================
  useEffect(() => {
    if (!company?.id) return;

    fetch(`${config.API_BASE_URL}/account/GetAllCategory/${company.id}`)
      .then((r) => r.json())
      .then((d) => setCategories(d || []))
      .catch(() => setCategories([]));

    // #2: whether the QR menu should show item descriptions (admin setting).
    fetch(`${config.API_BASE_URL}/qrmenu/desc-display/${company.id}`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.json) {
          try {
            const cfg = JSON.parse(d.json);
            setDescOnQr(cfg.qrMenu !== false);
            if (cfg.qrFoodTypes && typeof cfg.qrFoodTypes === 'object')
              setQrFoodTypes((p) => ({ ...p, ...cfg.qrFoodTypes }));
          } catch {
            /* keep default on */
          }
        }
      })
      .catch(() => {});

    setItemsLoading(true);
    fetch(`${config.API_BASE_URL}/Home/GetallItemsAsync/${company.id}`)
      .then((r) => r.json())
      .then((d) => setAllItems(d || []))
      .catch(() => setAllItems([]))
      .finally(() => setItemsLoading(false));

    // Variants (30 ml / 60 ml / 90 ml, Half / Full ...).
    //
    // Asked of the QR endpoint, not Home/GetSubcategories: that one requires a
    // login and answered 401 for a guest, so this map was always empty and the
    // menu showed a whisky at its bottle price with no pegs to choose. The QR
    // endpoint is anonymous and returns only active variants.
    fetch(`${config.API_BASE_URL}/qrmenu/item-variants/${company.id}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => {
        const map = {};
        (list || []).forEach((v) => {
          const pid = v.itemId;
          if (!pid) return;
          (map[pid] = map[pid] || []).push({
            id: v.id,
            name: v.name,
            price: Number(v.price) || 0,
          });
        });
        setVariantMap(map);
      })
      .catch(() => setVariantMap({}));

    // #16: item -> add-ons map (Extra Cheese, Extra Corn ...).
    fetch(`${config.API_BASE_URL}/qrmenu/item-addons/${company.id}`)
      .then((r) => r.json())
      .then((list) => {
        const map = {};
        (list || []).forEach((a) => {
          if (!a.itemId) return;
          (map[a.itemId] = map[a.itemId] || []).push({
            id: a.id,
            name: a.name,
            price: Number(a.price) || 0,
          });
        });
        setAddonMap(map);
      })
      .catch(() => setAddonMap({}));
  }, [company]);

  // ==================== DERIVED ====================
  // Categories the guest can see (hidden ones removed per QR settings).
  const visibleCategories = useMemo(
    () => categories.filter((c) => !hiddenCatIds.has(c.id)),
    [categories, hiddenCatIds],
  );

  const visibleItems = useMemo(() => {
    const q = itemSearch.toLowerCase().trim();
    // Everything this guest is allowed to see, before the search narrows it.
    const inScope = allItems.filter((item) => {
      if (hiddenCatIds.has(item.cateid)) return false;
      const ft = item.foodType || 'Veg';
      // Food-type filter: 'all' or an exact type (Veg/NonVeg/Egg/Vegan/Jain).
      if (foodFilter !== 'all' && ft !== foodFilter) return false;
      return true;
    });
    if (!q) return inScope;

    const typed = inScope.filter((item) => item.name?.toLowerCase().includes(q));
    if (typed.length > 0) return typed;
    // Spelled - or SAID - differently. A guest speaking "buni rayta" into the
    // mic, or typing "panir", should not be told the menu has no such dish.
    // Only as a fallback, so an exact search is never re-ordered by a guess.
    if (q.length < 3) return typed;
    return fuzzyFilter(inScope, itemSearch, (it) => it.name || '');
  }, [allItems, itemSearch, foodFilter, hiddenCatIds]);

  const groupedItems = useMemo(() => {
    const byId = {};
    visibleCategories.forEach((c) => (byId[c.id] = c.category_name));
    const groups = [];
    const index = {};
    visibleItems.forEach((item) => {
      const key = item.cateid;
      if (byId[key] == null) return; // belongs to a hidden category
      if (index[key] == null) {
        index[key] = groups.length;
        groups.push({ id: key, name: byId[key] || 'Menu', items: [] });
      }
      groups[index[key]].items.push(item);
    });
    return groups;
  }, [visibleItems, visibleCategories]);

  // Only categories that actually have items drive the left rail — a deactivated
  // (or empty) category has no items after the backend filter, so its tab is
  // hidden instead of scrolling to nothing.
  const railCategories = useMemo(() => {
    const withItems = new Set(groupedItems.map((g) => g.id));
    return visibleCategories.filter((c) => withItems.has(c.id));
  }, [visibleCategories, groupedItems]);

  useEffect(() => {
    if (groupedItems.length && activeCat == null)
      setActiveCat(groupedItems[0].id);
  }, [groupedItems, activeCat]);

  // The longest list on screen. The filling below stops here - without a
  // ceiling each fill would schedule the next one for ever.
  const longestGroup = useMemo(
    () => groupedItems.reduce((n, g) => Math.max(n, g.items.length), 0),
    [groupedItems],
  );

  // Draw the rest while the phone is doing nothing, so a guest who scrolls
  // quickly never catches up with the drawing. Idle work: it never competes
  // with the first paint, which is the one that has to be fast.
  useEffect(() => {
    if (view !== 'menu' || drawn >= longestGroup) return undefined;
    const idle = window.requestIdleCallback
      ? window.requestIdleCallback(() => setDrawn((d) => d + DRAW_BATCH * 3), { timeout: 1200 })
      : setTimeout(() => setDrawn((d) => d + DRAW_BATCH * 3), 600);
    return () => {
      if (window.cancelIdleCallback) window.cancelIdleCallback(idle);
      else clearTimeout(idle);
    };
  }, [view, drawn, longestGroup]);

  const cartArray = useMemo(() => Object.values(cart), [cart]);
  const cartCount = cartArray.reduce((s, c) => s + c.qty, 0);
  // #16: unit price = base/variant + selected add-ons.
  const lineUnit = (item, variant, addons) =>
    (variant ? variant.price : item.price || 0) +
    (addons || []).reduce((s, a) => s + (a.price || 0), 0);
  const cartTotal = cartArray.reduce(
    (s, c) => s + c.qty * lineUnit(c.item, c.variant, c.addons),
    0,
  );

  const keyFor = (item, variant, addons) =>
    `${item.id}_${variant ? variant.id : 0}_${(addons || [])
      .map((a) => a.id)
      .sort((x, y) => x - y)
      .join('-')}`;
  const qtyInCart = (item) =>
    cartArray.filter((c) => c.item.id === item.id).reduce((s, c) => s + c.qty, 0);

  // ── scroll-spy handlers ──
  const scrollToCat = (id) => {
    const el = sectionRefs.current[id];
    const cont = scrollRef.current;
    if (el && cont)
      cont.scrollTo({ top: el.offsetTop - cont.offsetTop - 8, behavior: 'smooth' });
  };
  const handleSpy = () => {
    const cont = scrollRef.current;
    if (!cont) return;

    // Coming to the end of what is drawn - draw more. This is the "yield"
    // side of the batching above.
    //
    // The ceiling matters: once the longest category is fully drawn there is
    // nothing left to add, and without it every single scroll event kept
    // raising the number. Returning the same value tells React there is
    // nothing to do, so the list is not rebuilt for a count that changes
    // nothing on screen.
    if (cont.scrollTop + cont.clientHeight > cont.scrollHeight - 500) {
      setDrawn((d) => (d >= longestGroup ? d : d + DRAW_BATCH));
    }

    const pos = cont.scrollTop + 16;
    let cur = groupedItems[0]?.id;
    for (const g of groupedItems) {
      const el = sectionRefs.current[g.id];
      if (el && el.offsetTop - cont.offsetTop <= pos) cur = g.id;
    }
    if (cur != null) setActiveCat(cur);
  };

  // ==================== CART OPS ====================
  const addToCart = (item, variant = null, addons = []) => {
    const k = keyFor(item, variant, addons);
    setCart((prev) => ({
      ...prev,
      [k]: { item, variant, addons, qty: (prev[k]?.qty || 0) + 1 },
    }));
  };
  const decFromCart = (k) => {
    setCart((prev) => {
      const next = { ...prev };
      if (!next[k]) return next;
      const q = next[k].qty - 1;
      if (q <= 0) delete next[k];
      else next[k] = { ...next[k], qty: q };
      return next;
    });
  };
  const onAdd = (item) => {
    if (!canOrder) return;
    const hasV = (variantMap[item.id] || []).length > 0;
    const hasA = (addonMap[item.id] || []).length > 0;
    // `picked` is separate from `variant` because null now means two different
    // things: nothing chosen yet, and the PLAIN ITEM chosen on purpose.
    if (hasV || hasA) setCustomize({ item, variant: null, addons: [], picked: false });
    else addToCart(item, null, []);
  };

  // ==================== PLACE ORDER (reuses SaveOrder + KOT) ====
  const getItemsByDepartment = () => {
    const grouped = {};
    cartArray.forEach(({ item, variant, addons, qty }) => {
      const dept = item.department || 'General';
      (grouped[dept] = grouped[dept] || []).push({ item, variant, addons, qty });
    });
    return grouped;
  };

  // ── Order tracking ────────────────────────────────────────────────────
  // Polled, not pushed: this page has no login, so it cannot join the
  // notification hub - the hub identifies a client by its token and a guest
  // has none. Every 10s against one small endpoint, and only while an order
  // is actually pending.
  // { orderId, batchId } of the guest's last submission.
  const [trackId, setTrackId] = useState(null);
  const [trackStatus, setTrackStatus] = useState(null);

  useEffect(() => {
    if (!trackId) return undefined;
    let stop = false;
    const tick = async () => {
      try {
        const res = await fetch(
          `${config.API_BASE_URL}/Home/QrOrderStatus/${trackId.orderId}?batch=${trackId.batchId || 0}`,
        );
        if (!res.ok) return;
        const d = await res.json();
        if (stop) return;
        setTrackStatus(d);
        // Settled either way - stop asking.
        if (d.status === 'Approved' || d.status === 'Rejected') stop = true;
      } catch {
        /* the next tick tries again */
      }
    };
    tick();
    const t = setInterval(() => { if (!stop) tick(); }, 10000);
    return () => { stop = true; clearInterval(t); };
  }, [trackId]);

  const sendToPrinterAPI = async () => {
    const grouped = getItemsByDepartment();
    const machineId = window.location.hostname;
    const dateTime = new Date().toLocaleString();
    for (const [dept, deptItems] of Object.entries(grouped)) {
      let kotNo = localStorage.getItem('kotNo') || 1;
      localStorage.setItem('kotNo', parseInt(kotNo) + 1);
      const itemsText = deptItems
        .map(({ item, variant, addons, qty }) => {
          const q = String(qty).padEnd(3, ' ');
          const addonTxt =
            (addons || []).length > 0
              ? ' +' + addons.map((a) => a.name).join('+')
              : '';
          const nm = `${item.name}${variant ? ' (' + variant.name + ')' : ''}${addonTxt}`
            .substring(0, 24)
            .padEnd(24, ' ');
          return `${q}| ${nm}`;
        })
        .join('\n');
      const divider = '--------------------------------\n';
      const data =
        '\x1B\x40\x1B\x61\x01' +
        (company?.name || 'HOTEL') +
        '\n' +
        divider +
        '\x1B\x45\x01KITCHEN ORDER TICKET\n\x1B\x45\x00' +
        divider +
        `KOT  : ${kotNo}\nDEPT : ${dept}\nTABLE: ${selectedTableName}\nTIME : ${dateTime}\n` +
        divider +
        'QTY | ITEM\n' +
        divider +
        itemsText +
        '\n' +
        divider +
        `NOTES:\n${notes || '-'}\n` +
        divider +
        '\x1B\x61\x01***** THANK YOU *****\n\n\n\n\n\n\n\x1D\x56\x00';
      await fetch(`${config.API_BASE_URL}/Service/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'KOT',
          content: data,
          machineId,
          companyid: company?.id,
          printerName: dept,
        }),
      });
    }
  };

  const buildPayload = () => ({
    BookingId: 0,
    RoomNo: 0,
    Tableno: selectedTableName,
    Items: cartArray.map(({ item, variant, addons, qty }) => ({
      ItemId: item.id,
      SubcategoryId: variant ? variant.id : 0,
      Qty: qty,
      // #16: unit price includes selected add-ons so the bill reflects them.
      Price:
        (addons || []).length > 0
          ? lineUnit(item, variant, addons)
          : variant
            ? variant.price
            : null,
    })),
    Notes: notes.trim() || '',
    CompanyId: company?.id,
    userid: 1,
  });

  // Save the order. Returns its id and the guest's batch on it - the items
  // join the order already open on the table / room (0s on failure).
  const saveOrder = async () => {
    const res = await fetch(`${config.API_BASE_URL}/account/SaveOrder`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildPayload()),
    });
    const d = await res.json().catch(() => ({}));
    // A refusal carries its reason (e.g. out of stock) - pass it on rather
    // than let it turn into a bare "Failed to place order".
    if (!res.ok) throw new Error(d?.message || 'Could not save the order.');
    return { orderId: d?.orderId || d?.id || 0, batchId: d?.batchId || 0 };
  };

  // Pay-at-counter flow: save and wait.
  //
  // The KOT is NOT printed from here any more. A QR order is a request until
  // the floor approves it on the QR Orders screen, and that approval is what
  // deducts the stock and sends the ticket. Printing here would put food on the
  // pass for an order nobody had agreed to make.
  // Refused here as well as on the server. The ADD buttons are hidden while
  // ordering is off, but a cart filled BEFORE a switch to a blocked room, or
  // before the access answer arrived, would otherwise still post.
  const refuseIfBlocked = () => {
    if (canOrder) return false;
    setSnackbar({
      open: true,
      message:
        blockedReason
        || `Ordering is not available for this ${isRoom ? 'room' : 'table'}.`,
      severity: 'error',
    });
    return true;
  };

  const placeOrder = async () => {
    if (!cartArray.length) return;
    if (refuseIfBlocked()) return;
    setSubmitting(true);
    try {
      const placed = await saveOrder();
      if (!placed.orderId) throw new Error();
      // Watch it from here. A guest who is told "waiting for confirmation" and
      // then hears nothing walks to the counter to ask - which is the whole
      // thing the QR was meant to save.
      setTrackId(placed);
      setSnackbar({
        open: true,
        // "sent", not "placed" - the kitchen has not accepted it yet, and
        // telling a guest otherwise is how a table waits for food that was
        // never started.
        message: 'Order sent! Waiting for the counter to confirm.',
        severity: 'success',
      });
      setCart({});
      setNotes('');
      setCartOpen(false);
    } catch (e) {
      // The server's reason when it gave one (e.g. out of stock).
      setSnackbar({ open: true, message: e?.message || 'Failed to place order', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (!cartArray.length) return;
    if (refuseIfBlocked()) return;
    if (paymentEnabled) {
      setCartOpen(false);
      setPayOpen(true); // choose gateway
    } else {
      placeOrder();
    }
  };

  // Auto-submit a hidden form to CC Avenue (same helper as the subscription
  // payment). Navigates the browser to the gateway.
  const submitToCCAvenue = (gatewayUrl, encRequest, accessCode) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = gatewayUrl;
    const add = (n, v) => {
      const i = document.createElement('input');
      i.type = 'hidden';
      i.name = n;
      i.value = v;
      form.appendChild(i);
    };
    add('encRequest', encRequest);
    add('access_code', accessCode);
    document.body.appendChild(form);
    form.submit();
  };

  // Online-payment flow: save the order (so the kitchen has it), print KOT,
  // then hand off to the chosen gateway. On success the backend marks the order
  // paid and redirects back to this page with ?paystatus=success.
  const payAndPlace = async (gateway) => {
    setPayOpen(false);
    if (refuseIfBlocked()) return;
    setSubmitting(true);
    try {
      const { orderId } = await saveOrder();
      if (!orderId) throw new Error('Could not save the order.');
      // No KOT here either - see placeOrder. Approval prints it.

      const client =
        clientFromUrl || localStorage.getItem('client') || 'Demo';
      const returnUrl =
        `${window.location.origin}${window.location.pathname}` +
        `?client=${encodeURIComponent(client)}&tablename=${encodeURIComponent(
          selectedTableName,
        )}`;
      const body = JSON.stringify({
        CompanyId: company?.id,
        OrderId: orderId,
        // Charge only THIS guest's cart — orders merge into one running table
        // order, so the backend must not charge the whole table.
        Amount: Number(cartTotal.toFixed(2)),
        ReturnUrl: returnUrl,
      });

      if (gateway === 'ccavenue') {
        const res = await fetch(
          `${config.API_BASE_URL}/orderpay/ccavenue/initiate`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
          },
        );
        const d = await res.json();
        if (!d?.success) throw new Error(d?.message || 'Payment could not start.');
        submitToCCAvenue(d.gatewayUrl, d.encRequest, d.accessCode);
      } else {
        const res = await fetch(
          `${config.API_BASE_URL}/orderpay/allupi/initiate`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
          },
        );
        const d = await res.json();
        if (!d?.success || !d?.paymentUrl)
          throw new Error(d?.message || 'Payment could not start.');
        window.location.href = d.paymentUrl;
      }
      // On success the browser navigates away to the gateway; keep the spinner.
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.message || 'Payment failed to start',
        severity: 'error',
      });
      setSubmitting(false);
    }
  };

  // ==================== THE DISH LIST ====================
  //
  // Built once per change of the data it actually shows, and NOT rebuilt when
  // some unrelated bit of the screen changes.
  //
  // Why this matters: opening the customise dialog is one setState, and React
  // answers a setState by re-rendering the whole component. That meant every
  // drawn card in every category - a couple of hundred of them, each carrying
  // ten `sx` objects that emotion has to turn back into CSS - was rebuilt
  // before the dialog could appear. On a phone that was the two and a half
  // seconds between the tap and the popup (Chrome reported INP 2,560 ms).
  //
  // `customize` is deliberately absent from the dependency list below, so
  // opening the dialog, picking a size or ticking an add-on hands React the
  // very same element tree for the list, and React skips it entirely.
  //
  // Anything the cards READ must be in the list: miss one and a card goes on
  // showing yesterday's price. The handlers inside close over this render's
  // `cart`, which is why `cart` is a dependency even though the adders use the
  // functional `setCart(prev => ...)` form.
  //
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const menuSections = useMemo(
    () =>
      itemsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: BRAND }} />
        </Box>
      ) : groupedItems.length === 0 ? (
        <Typography sx={{ textAlign: 'center', color: '#9ca3af', py: 8 }}>
          No items found.
        </Typography>
      ) : (
        groupedItems.map((group) => (
          <Box
            key={group.id}
            ref={(el) => (sectionRefs.current[group.id] = el)}
            sx={{ mb: 3 }}>
            {/* Prominent category heading */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.75 }}>
              <Typography
                sx={{
                  fontSize: '1.35rem',
                  fontWeight: 800,
                  letterSpacing: 0.3,
                  color: '#111827',
                }}>
                {group.name}
              </Typography>
              <Box sx={{ flex: 1, height: '2px', bgcolor: '#e5e7eb', borderRadius: 2 }} />
            </Box>

            {group.items.slice(0, drawn).map((item) => {
              const inCart = qtyInCart(item);
              const itemVariants = variantMap[item.id] || [];
              const itemAddons = addonMap[item.id] || [];
              const customizable = itemVariants.length > 0 || itemAddons.length > 0;
              // A bottle of whisky is priced at 25,000 and poured at 800 a
              // peg. Showing the bottle price and hiding the pegs behind a
              // button reads as "we do not sell pegs" - and on a view-only
              // QR, where there is no ADD button at all, the guest could
              // never reach them.
              const cheapest = itemVariants.length
                ? Math.min(...itemVariants.map((v) => Number(v.price) || 0))
                : null;
              return (
                <Box
                  key={item.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    bgcolor: '#fff',
                    borderRadius: 3,
                    p: 1.5,
                    mb: 1.5,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                  }}>
                  {/* This box used to carry content-visibility: auto with a
                      110px placeholder height. It skipped the layout of
                      off-screen cards, but a card with a description, size
                      chips and an add-ons line is nearer 180px - so every
                      card grew by seventy-odd pixels at the moment it came
                      into view, and everything below it jumped. Chrome scored
                      the page CLS 1.03 (anything above 0.1 is "poor").
                      The batching in `drawn` and the lazy images below already
                      keep the mounting cost down; laying out text is cheap.
                      The saving was not worth a menu that moves under the
                      guest's thumb. */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <FoodMark type={item.foodType || 'Veg'} />
                    <Typography fontWeight={700} sx={{ color: '#1f2937', mt: 0.5 }}>
                      {item.name}
                    </Typography>
                    {descOnQr && item.description && (
                      <Typography
                        sx={{
                          color: '#6b7280',
                          fontSize: 12.5,
                          mt: 0.25,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}>
                        {item.description}
                      </Typography>
                    )}
                    <Typography sx={{ color: '#111827', fontWeight: 600, mt: 0.5 }}>
                      {cheapest != null ? (
                        <>
                          {Companycurrency}
                          {cheapest}
                          <Box component='span' sx={{ color: '#6b7280', fontWeight: 500, fontSize: 12.5 }}>
                            {' '}onwards
                          </Box>
                        </>
                      ) : (
                        <>
                          {Companycurrency}
                          {item.price}
                        </>
                      )}
                    </Typography>

                    {/* Every size, with its own price, on the card itself. */}
                    {itemVariants.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                        {itemVariants.slice(0, 4).map((v) => (
                          <Box
                            key={v.id}
                            onClick={() => canOrder && addToCart(item, v, [])}
                            sx={{
                              px: 0.9,
                              py: 0.25,
                              borderRadius: 1.5,
                              border: '1px solid #e5e7eb',
                              bgcolor: '#f9fafb',
                              fontSize: 11.5,
                              color: '#374151',
                              cursor: canOrder ? 'pointer' : 'default',
                              whiteSpace: 'nowrap',
                            }}>
                            {v.name} · {Companycurrency}{v.price}
                          </Box>
                        ))}
                        {itemVariants.length > 4 && (
                          <Box sx={{ fontSize: 11.5, color: '#9ca3af', alignSelf: 'center' }}>
                            +{itemVariants.length - 4} more
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Add-ons are extras on top, so they are named rather
                        than priced here - the price depends on what is
                        ticked, and that happens in the dialog. */}
                    {itemAddons.length > 0 && (
                      <Typography sx={{ color: '#6b7280', fontSize: 11.5, mt: 0.5 }}>
                        Add-ons: {itemAddons.slice(0, 3).map((a) => a.name).join(', ')}
                        {itemAddons.length > 3 ? ` +${itemAddons.length - 3}` : ''}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ width: 110, flexShrink: 0, position: 'relative' }}>
                    {item.image ? (
                      <Box
                        component='img'
                        src={resolveImg(item.image)}
                        alt={item.name}
                        // A forty-dish category used to fire forty image
                        // requests the moment it rendered, all fighting
                        // for one phone's connection. The browser now
                        // fetches each one as it comes near the screen.
                        loading='lazy'
                        decoding='async'
                        sx={{
                          width: '100%',
                          height: 90,
                          objectFit: 'cover',
                          borderRadius: 2,
                          bgcolor: '#f3f4f6',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: 90,
                          borderRadius: 2,
                          bgcolor: '#f3f4f6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                        <RestaurantMenuIcon sx={{ color: '#d1d5db' }} />
                      </Box>
                    )}

                    {canOrder &&
                      (inCart > 0 ? (
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: customizable ? 12 : -10,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: '#fff',
                            border: `1px solid ${BRAND}`,
                            borderRadius: 2,
                            boxShadow: 2,
                          }}>
                          <IconButton
                            size='small'
                            onClick={() => {
                              const line = cartArray
                                .filter((c) => c.item.id === item.id)
                                .slice(-1)[0];
                              if (line)
                                decFromCart(
                                  keyFor(line.item, line.variant, line.addons),
                                );
                            }}>
                            <RemoveIcon fontSize='small' sx={{ color: BRAND }} />
                          </IconButton>
                          <Typography fontWeight={700} sx={{ color: BRAND, px: 0.5 }}>
                            {inCart}
                          </Typography>
                          <IconButton size='small' onClick={() => onAdd(item)}>
                            <AddIcon fontSize='small' sx={{ color: BRAND }} />
                          </IconButton>
                        </Box>
                      ) : (
                        <Button
                          onClick={() => onAdd(item)}
                          sx={{
                            position: 'absolute',
                            bottom: customizable ? 12 : -10,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            bgcolor: '#fff',
                            color: BRAND,
                            fontWeight: 800,
                            border: `1px solid ${BRAND}`,
                            borderRadius: 2,
                            px: 3,
                            boxShadow: 2,
                            '&:hover': { bgcolor: '#fff' },
                          }}>
                          ADD
                        </Button>
                      ))}

                    {customizable && (
                      <Typography
                        sx={{
                          position: 'absolute',
                          bottom: -8,
                          left: 0,
                          right: 0,
                          textAlign: 'center',
                          fontSize: 10,
                          color: '#9ca3af',
                        }}>
                        customizable
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))
      ),
    [
      itemsLoading,
      groupedItems,
      drawn,
      variantMap,
      addonMap,
      cart,
      canOrder,
      descOnQr,
      Companycurrency,
    ],
  );

  // ==================== LOADING GATE ====================
  //
  // The QR names a restaurant nobody has. Said plainly, with the code on
  // screen: it is the one thing the counter needs to fix the card.
  if (unknownQr) {
    return (
      <Box
        sx={{
          minHeight: '100dvh',
          bgcolor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 4,
          textAlign: 'center',
          gap: 1,
        }}>
        <RestaurantMenuIcon sx={{ fontSize: 56, color: '#cbd5e1' }} />
        <Typography variant='h6' fontWeight={800} sx={{ color: '#1f2937' }}>
          This QR code is not linked to a restaurant
        </Typography>
        <Typography sx={{ color: '#6b7280', maxWidth: 340 }}>
          Please ask at the counter — they can print a new one. Nothing is wrong
          with your phone.
        </Typography>
        <Typography sx={{ color: '#9ca3af', fontSize: 12, mt: 1 }}>
          Code on this card: {clientFromUrl || '—'}
          {tableNameFromUrl ? ` · ${tableNameFromUrl}` : ''}
        </Typography>
      </Box>
    );
  }

  if (!companyReady) {
    return (
      <Box
        sx={{
          height: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#fff',
        }}>
        <CircularProgress sx={{ color: BRAND }} />
      </Box>
    );
  }

  // A room QR said "Table 101" on every screen, which reads oddly in a
  // bedroom. The server already tells us which one this is; the word follows.
  const spotWord = isRoom ? 'Room' : 'Table';

  const branchLabel = company?.city || company?.state || '';
  const fullName = branchLabel
    ? `${company?.name} - ${branchLabel}`
    : company?.name;
  // Restaurant brand: use the company's own logo when uploaded, else fall back
  // to the "dine" restaurant logo (instead of goerp/no logo).
  const logoSrc = resolveImg(company?.logo) || '/restaurant-logo.png';

  // ==================== LANDING (reference) ====================
  if (view === 'landing') {
    return (
      <Box
        sx={{
          minHeight: '100dvh',
          bgcolor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          py: 6,
          textAlign: 'center',
        }}>
        {logoSrc ? (
          <Box
            component='img'
            src={logoSrc}
            alt={company?.name}
            // A fixed height, not a max-height. With only a maximum the box is
            // nothing until the logo arrives, and then it is 150px tall - the
            // name, the table and the button all drop down the screen at once,
            // which is a layout shift big enough to score the page "poor" on
            // its own. Reserving the space costs nothing and the logo is
            // centred inside it either way.
            sx={{
              width: '100%',
              maxWidth: 200,
              height: 150,
              objectFit: 'contain',
              mb: 1,
            }}
          />
        ) : (
          <RestaurantMenuIcon sx={{ fontSize: 72, color: BRAND, mb: 1 }} />
        )}

        <Typography variant='h5' fontWeight={800} sx={{ color: '#1f2937' }}>
          {fullName}
        </Typography>

        {company?.openingHours && (
          <Typography sx={{ color: '#6b7280', mt: 0.5 }}>
            {company.openingHours}
          </Typography>
        )}

        <Box sx={{ mt: 5 }}>
          <Typography variant='h4' fontWeight={800} sx={{ color: '#111827' }}>
            {selectedTableName ? `${spotWord} ${selectedTableName}` : 'Welcome'}
          </Typography>
          {selectedTableName && (
            <Button
              onClick={() => {
                setSwitchValue(selectedTableName);
                setSwitchOpen(true);
              }}
              sx={{ color: BRAND, fontWeight: 700, mt: 0.5 }}>
              SWITCH
            </Button>
          )}
        </Box>

        <Button
          fullWidth
          variant='contained'
          onClick={() => setView('menu')}
          sx={{
            mt: 6,
            maxWidth: 320,
            py: 1.5,
            borderRadius: 2,
            bgcolor: BRAND,
            fontWeight: 700,
            fontSize: '1rem',
            '&:hover': { bgcolor: '#0f1a30' },
          }}>
          VIEW MENU
        </Button>

        {/* A ROOM's live verdict, on the page the guest lands on after the
            scan - never on the printed card, which lives in the room for
            years while this changes with every check-in and check-out. */}
        {isRoom && accessChecked ? (
          <Box
            sx={{
              mt: 2,
              maxWidth: 320,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.75,
            }}>
            <Chip
              icon={qrEnabled ? <CheckCircleIcon /> : <BlockIcon />}
              label={qrEnabled ? 'Active' : 'Blocked'}
              sx={{
                fontWeight: 800,
                letterSpacing: '0.02em',
                color: '#fff',
                bgcolor: qrEnabled ? '#16A34A' : '#DC2626',
                '& .MuiChip-icon': { color: '#fff' },
              }}
            />
            <Typography
              sx={{
                color: qrEnabled ? '#15803D' : '#c0392b',
                fontSize: 13,
                textAlign: 'center',
              }}>
              {qrEnabled
                ? 'This room is checked in. You can order from the menu.'
                : blockedReason
                  || 'This room is not checked in. Please contact the front desk.'}
            </Typography>
          </Box>
        ) : (
          !qrEnabled && (
            <Typography sx={{ color: '#c0392b', mt: 2, fontSize: 13, maxWidth: 320 }}>
              {blockedReason || 'Ordering is currently disabled for this table.'}
            </Typography>
          )
        )}

        <SwitchTableDialog
          open={switchOpen}
          value={switchValue}
          onChange={setSwitchValue}
          onClose={() => setSwitchOpen(false)}
          onSave={() => {
            setSelectedTableName(switchValue.trim());
            setSwitchOpen(false);
          }}
        />
      </Box>
    );
  }

  // ==================== MENU ====================
  return (
    <Box
      sx={{
        height: '100dvh',
        overflow: 'hidden',
        bgcolor: '#f3f4f6',
        display: 'flex',
        flexDirection: 'column',
      }}>
      {/* Header: logo + name + branch + table */}
      <Box
        sx={{
          bgcolor: '#fff',
          px: 2,
          pt: 1.5,
          pb: 1,
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          flexShrink: 0,
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            {logoSrc && (
              <Box
                component='img'
                src={logoSrc}
                alt=''
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1.5,
                  objectFit: 'contain',
                  flexShrink: 0,
                  border: '1px solid #f1f1f1',
                }}
              />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={800} noWrap sx={{ color: '#111827', lineHeight: 1.2 }}>
                {company?.name}
              </Typography>
              <Typography sx={{ color: '#6b7280', fontSize: 12.5 }} noWrap>
                {branchLabel ? `${branchLabel} · ` : ''}{spotWord}{' '}
                {selectedTableName || '-'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setView('landing')} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Veg toggle + search */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <Select
            size='small'
            value={foodFilter}
            onChange={(e) => setFoodFilter(e.target.value)}
            sx={{
              flexShrink: 0,
              borderRadius: 5,
              bgcolor: '#fff',
              minWidth: 70,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                gap: 0.4,
                py: 0.6,
                pl: 1,
              },
            }}
            renderValue={(val) =>
              val === 'all' ? (
                <Box sx={{ display: 'flex', gap: 0.4 }}>
                  {FOOD_TYPE_ORDER.filter(([k]) => qrFoodTypes[k])
                    .slice(0, 2)
                    .map(([k]) => (
                      <FoodMark key={k} type={k} />
                    ))}
                </Box>
              ) : (
                <FoodMark type={val} />
              )
            }>
            <MenuItem value='all'>ALL</MenuItem>
            {FOOD_TYPE_ORDER.filter(([k]) => qrFoodTypes[k]).map(([k, label]) => (
              <MenuItem key={k} value={k}>
                <FoodMark type={k} />
                <Box component='span' sx={{ ml: 0.75 }}>
                  {label}
                </Box>
              </MenuItem>
            ))}
          </Select>
          <TextField
            fullWidth
            size='small'
            placeholder={listening ? 'Listening… say the dish name' : 'Search in Menu'}
            value={itemSearch}
            onChange={(e) => setItemSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon sx={{ color: '#9ca3af' }} />
                </InputAdornment>
              ),
              // Say the dish instead of spelling it. Shown only where the
              // browser can actually listen, and the match is phonetic, so
              // "buni rayta" still finds Bundi Raita.
              endAdornment: voiceSupported ? (
                <InputAdornment position='end'>
                  <IconButton
                    size='small'
                    aria-label={listening ? 'Stop listening' : 'Search by voice'}
                    onClick={voiceSearch}
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      bgcolor: listening ? '#ef4444' : 'transparent',
                      '& .MuiSvgIcon-root': {
                        color: listening ? '#fff' : '#2563eb',
                      },
                      '&:hover': {
                        bgcolor: listening ? '#ef4444' : 'rgba(37,99,235,0.1)',
                      },
                      '@keyframes micPulseQr': {
                        '0%': { boxShadow: '0 0 0 0 rgba(239,68,68,0.5)' },
                        '100%': { boxShadow: '0 0 0 10px rgba(239,68,68,0)' },
                      },
                      animation: listening
                        ? 'micPulseQr 1s ease-out infinite'
                        : 'none',
                    }}>
                    <MicIcon sx={{ fontSize: 19 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: { borderRadius: 5, bgcolor: '#fff' },
            }}
          />
        </Box>
      </Box>

      {/* View-only banner */}
      {!canOrder && (
        <Box
          sx={{
            bgcolor: '#fff7ed',
            color: '#9a3412',
            px: 2,
            py: 1,
            fontSize: 13,
            textAlign: 'center',
            flexShrink: 0,
          }}>
          {/* The room reason comes first when there is one - "ordering is
              disabled" tells a guest nothing they can act on, while "this room
              is not checked in" sends them to the desk. */}
          {blockedReason
            ? blockedReason
            : qrEnabled
              ? 'Menu only — please call a waiter to order.'
              : `Ordering is disabled for this ${isRoom ? 'room' : 'table'}.`}
        </Box>
      )}

      {/* Body: left category rail + right items */}
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Left vertical category navigation */}
        <Box
          sx={{
            width: { xs: 80, sm: 104 },
            flexShrink: 0,
            bgcolor: '#fff',
            borderRight: '1px solid #eef0f3',
            overflowY: 'auto',
            py: 1,
            '&::-webkit-scrollbar': { display: 'none' },
          }}>
          {railCategories.map((cat) => {
            const active = activeCat === cat.id;
            const img = resolveImg(cat.cateimg);
            return (
              <Box
                key={cat.id}
                onClick={() => scrollToCat(cat.id)}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 0.5,
                  py: 1.25,
                  cursor: 'pointer',
                  transition: 'background .2s',
                  bgcolor: active ? 'rgba(22,36,63,0.06)' : 'transparent',
                  '&:before': active
                    ? {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 8,
                        bottom: 8,
                        width: 3,
                        borderRadius: 3,
                        bgcolor: BRAND,
                      }
                    : {},
                }}>
                <Avatar
                  src={img || undefined}
                  variant='rounded'
                  sx={{
                    width: 46,
                    height: 46,
                    bgcolor: active ? BRAND : '#eef1f6',
                    color: active ? '#fff' : '#9aa3b2',
                    transition: 'all .2s',
                    transform: active ? 'scale(1.06)' : 'scale(1)',
                  }}>
                  {!img && <RestaurantMenuIcon fontSize='small' />}
                </Avatar>
                <Typography
                  sx={{
                    fontSize: 11,
                    lineHeight: 1.15,
                    textAlign: 'center',
                    fontWeight: active ? 800 : 600,
                    color: active ? BRAND : '#4b5563',
                    px: 0.25,
                  }}>
                  {cat.category_name}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Right scrollable items */}
        <Box
          ref={scrollRef}
          onScroll={handleSpy}
          sx={{
            flex: 1,
            overflowY: 'auto',
            position: 'relative',
            px: 1.75,
            py: 2,
            pb: 12,
          }}>
          {menuSections}
        </Box>
      </Box>

      {/* Cart bar */}
      {canOrder && cartCount > 0 && (
        <Box sx={{ position: 'fixed', bottom: 16, left: 16, right: 16, zIndex: 20 }}>
          <Button
            fullWidth
            variant='contained'
            onClick={() => setCartOpen(true)}
            sx={{
              bgcolor: BRAND,
              py: 1.4,
              borderRadius: 3,
              fontWeight: 700,
              justifyContent: 'space-between',
              '&:hover': { bgcolor: '#0f1a30' },
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Badge badgeContent={cartCount} color='error'>
                <ShoppingCartIcon />
              </Badge>
              View Cart
            </Box>
            <span>
              {Companycurrency}
              {cartTotal.toFixed(0)}
            </span>
          </Button>
        </Box>
      )}

      {/* Cart drawer */}
      <Drawer
        anchor='bottom'
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        PaperProps={{
          sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '85vh' },
        }}>
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}>
            <Typography variant='h6' fontWeight={800}>
              Your Order · {spotWord} {selectedTableName}
            </Typography>
            <IconButton onClick={() => setCartOpen(false)} size='small'>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 1 }} />

          {cartArray.map((c) => {
            const k = keyFor(c.item, c.variant, c.addons);
            const unit = lineUnit(c.item, c.variant, c.addons);
            return (
              <Box
                key={k}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1,
                }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <FoodMark type={c.item.foodType || 'Veg'} />
                    <Typography fontWeight={600} noWrap>
                      {c.item.name}
                      {c.variant ? ` (${c.variant.name})` : ''}
                    </Typography>
                  </Box>
                  {(c.addons || []).length > 0 && (
                    <Typography sx={{ color: '#9ca3af', fontSize: 11 }} noWrap>
                      + {c.addons.map((a) => a.name).join(', ')}
                    </Typography>
                  )}
                  <Typography sx={{ color: '#6b7280', fontSize: 13 }}>
                    {Companycurrency}
                    {unit}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: `1px solid ${BRAND}`,
                    borderRadius: 2,
                  }}>
                  <IconButton size='small' onClick={() => decFromCart(k)}>
                    <RemoveIcon fontSize='small' sx={{ color: BRAND }} />
                  </IconButton>
                  <Typography fontWeight={700} sx={{ px: 0.5, color: BRAND }}>
                    {c.qty}
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => addToCart(c.item, c.variant, c.addons)}>
                    <AddIcon fontSize='small' sx={{ color: BRAND }} />
                  </IconButton>
                </Box>
              </Box>
            );
          })}

          <TextField
            fullWidth
            multiline
            rows={2}
            size='small'
            placeholder='Special instructions / notes'
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ mt: 1.5 }}
          />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 2,
            }}>
            <Typography fontWeight={800}>Total</Typography>
            <Typography fontWeight={800} sx={{ color: BRAND }}>
              {Companycurrency}
              {cartTotal.toFixed(2)}
            </Typography>
          </Box>

          <Button
            fullWidth
            variant='contained'
            disabled={submitting || !cartArray.length || !canOrder}
            onClick={handleSubmit}
            sx={{
              mt: 2,
              py: 1.3,
              borderRadius: 2,
              bgcolor: BRAND,
              fontWeight: 700,
              '&:hover': { bgcolor: '#0f1a30' },
            }}>
            {submitting ? (
              <CircularProgress size={22} color='inherit' />
            ) : (
              'Place Order'
            )}
          </Button>
        </Box>
      </Drawer>

      {/* #16: Customize — variant (radio) + add-ons (checkboxes) */}
      <Dialog
        open={!!customize}
        onClose={() => setCustomize(null)}
        fullWidth
        maxWidth='xs'
        // MUI's default is 225 ms in. On a tap that is meant to feel instant
        // it reads as hesitation, so the dialog comes in faster than it goes
        // out - quick to answer the thumb, unhurried on the way out.
        transitionDuration={{ enter: 140, exit: 100 }}>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {customize?.item?.name}
          <Typography sx={{ color: '#6b7280', fontSize: 13 }}>
            Customize your item
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {/* Variants */}
          {customize && (variantMap[customize.item.id] || []).length > 0 && (
            <>
              <Typography fontWeight={700} sx={{ mb: 0.5 }}>
                Choose an option
              </Typography>

              {/* THE PLAIN ITEM, as an option of its own.
                  Only the variants used to be listed, so a beer that also sold
                  as a 6-bottle bucket could ONLY be bought as the bucket - the
                  single bottle had a price and no way to reach it.
                  Hidden when the item has no price of its own: a whisky priced
                  at 0 and sold by the peg would otherwise offer a ₹0 line. */}
              {Number(customize.item.price) > 0 && (
                <Box
                  onClick={() =>
                    setCustomize((c) => ({ ...c, variant: null, picked: true }))
                  }
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1,
                    px: 1,
                    mb: 0.5,
                    borderRadius: 1.5,
                    border: `1px solid ${
                      customize.picked && !customize.variant ? BRAND : '#eee'
                    }`,
                    bgcolor:
                      customize.picked && !customize.variant
                        ? 'rgba(22,36,63,0.05)'
                        : 'transparent',
                    cursor: 'pointer',
                  }}>
                  <Typography fontWeight={600}>{customize.item.name}</Typography>
                  <Typography fontWeight={700} sx={{ color: BRAND }}>
                    {Companycurrency}
                    {customize.item.price}
                  </Typography>
                </Box>
              )}

              {(variantMap[customize.item.id] || []).map((v) => {
                const sel = customize.variant?.id === v.id;
                return (
                  <Box
                    key={v.id}
                    onClick={() =>
                      setCustomize((c) => ({ ...c, variant: v, picked: true }))
                    }
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1,
                      px: 1,
                      mb: 0.5,
                      borderRadius: 1.5,
                      border: `1px solid ${sel ? BRAND : '#eee'}`,
                      bgcolor: sel ? 'rgba(22,36,63,0.05)' : 'transparent',
                      cursor: 'pointer',
                    }}>
                    <Typography fontWeight={600}>{v.name}</Typography>
                    <Typography fontWeight={700} sx={{ color: BRAND }}>
                      {Companycurrency}
                      {v.price}
                    </Typography>
                  </Box>
                );
              })}
            </>
          )}

          {/* Add-ons */}
          {customize && (addonMap[customize.item.id] || []).length > 0 && (
            <>
              <Typography fontWeight={700} sx={{ mt: 1.5, mb: 0.5 }}>
                Add-ons
              </Typography>
              {(addonMap[customize.item.id] || []).map((a) => {
                const checked = customize.addons.some((x) => x.id === a.id);
                return (
                  <Box
                    key={a.id}
                    onClick={() =>
                      setCustomize((c) => ({
                        ...c,
                        addons: checked
                          ? c.addons.filter((x) => x.id !== a.id)
                          : [...c.addons, a],
                      }))
                    }
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1,
                      px: 1,
                      cursor: 'pointer',
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: 0.5,
                          border: `2px solid ${checked ? BRAND : '#cbd5e1'}`,
                          bgcolor: checked ? BRAND : '#fff',
                          color: '#fff',
                          fontSize: 13,
                          textAlign: 'center',
                          lineHeight: '15px',
                        }}>
                        {checked ? '✓' : ''}
                      </Box>
                      <Typography fontWeight={600}>{a.name}</Typography>
                    </Box>
                    <Typography fontWeight={700} sx={{ color: BRAND }}>
                      +{Companycurrency}
                      {a.price}
                    </Typography>
                  </Box>
                );
              })}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomize(null)}>Cancel</Button>
          <Button
            variant='contained'
            // Waits for a CHOICE, not for a variant. Choosing the plain item
            // leaves variant null, which this used to read as "nothing picked
            // yet" and kept the button dead.
            disabled={
              customize &&
              (variantMap[customize.item.id] || []).length > 0 &&
              !customize.picked
            }
            onClick={() => {
              addToCart(customize.item, customize.variant, customize.addons);
              setCustomize(null);
            }}
            sx={{ bgcolor: BRAND, '&:hover': { bgcolor: '#0f1a30' } }}>
            Add{' '}
            {Companycurrency}
            {customize
              ? lineUnit(customize.item, customize.variant, customize.addons)
              : ''}
          </Button>
        </DialogActions>
      </Dialog>

      <SwitchTableDialog
        open={switchOpen}
        value={switchValue}
        onChange={setSwitchValue}
        onClose={() => setSwitchOpen(false)}
        onSave={() => {
          setSelectedTableName(switchValue.trim());
          setSwitchOpen(false);
        }}
      />

      {/* Payment gateway choice (same gateways as the subscription upgrade) */}
      <Dialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        fullWidth
        maxWidth='xs'>
        <DialogTitle sx={{ fontWeight: 800 }}>
          Pay {Companycurrency}
          {cartTotal.toFixed(2)}
          <Typography sx={{ color: '#6b7280', fontSize: 13 }}>
            Table {selectedTableName} · choose a payment method
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Button
            fullWidth
            variant='contained'
            onClick={() => payAndPlace('ccavenue')}
            sx={{
              mb: 1.5,
              py: 1.3,
              borderRadius: 2,
              bgcolor: BRAND,
              fontWeight: 700,
              '&:hover': { bgcolor: '#0f1a30' },
            }}>
            Pay with CC Avenue (Card / UPI / Netbanking)
          </Button>
          <Button
            fullWidth
            variant='outlined'
            onClick={() => payAndPlace('allupi')}
            sx={{
              py: 1.3,
              borderRadius: 2,
              fontWeight: 700,
              color: BRAND,
              borderColor: BRAND,
            }}>
            Pay with AllUPI (UPI QR)
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Order status, pinned to the bottom of the guest's screen.
          A snackbar disappears in four seconds; the wait for the counter does
          not, and a guest with nothing on screen walks over to ask - which is
          the trip the QR was meant to save. Stays until the answer comes. */}
      {trackStatus ? (
        <Box
          sx={{
            position: 'fixed',
            left: 12, right: 12, bottom: 12,
            zIndex: 1300,
            p: 1.5,
            borderRadius: 2,
            boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
            bgcolor:
              trackStatus.status === 'Approved' ? '#dcfce7'
              : trackStatus.status === 'Rejected' ? '#fee2e2'
              : '#fef3c7',
            border: '1px solid',
            borderColor:
              trackStatus.status === 'Approved' ? '#86efac'
              : trackStatus.status === 'Rejected' ? '#fca5a5'
              : '#fcd34d',
          }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
            {trackStatus.status === 'Approved' ? 'Order confirmed'
             : trackStatus.status === 'Rejected' ? 'Order not accepted'
             : 'Waiting for confirmation'}
            {' · #'}{trackStatus.orderId}
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', mt: 0.3 }}>
            {trackStatus.message}
          </Typography>
          {trackStatus.status !== 'Pending' ? (
            <Button size='small' sx={{ mt: 0.5 }} onClick={() => { setTrackId(null); setTrackStatus(null); }}>
              Dismiss
            </Button>
          ) : null}
        </Box>
      ) : null}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} variant='filled'>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function SwitchTableDialog({ open, value, onChange, onClose, onSave }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='xs'>
      <DialogTitle sx={{ fontWeight: 800 }}>Switch Table</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label='Table number'
          value={value}
          onChange={(e) => onChange(e.target.value)}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={onSave} disabled={!value.trim()}>
          Switch
        </Button>
      </DialogActions>
    </Dialog>
  );
}
